import { useEffect, useState, useMemo, useCallback } from "react";
import "./LandingPage.css";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/DataTable/DataTable";
import { useTableControls } from "../../hooks/useTableControls";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { getTextByLang } from "../../utils/formUtils";
import { showErrorAlert } from "../../utils/showErrorAlert";
import { useDeleteCatalog } from "../../hooks/catalogs/useDeleteCatalog";
import OfferDetails from "../OfferingData/OfferingDetails";
import { useGetCatalog } from "../../hooks/catalogs/useGetCatalog";
import { useGetVocabsPaginated } from "../../hooks/vocabularies/useGetVocab";
import { extractIdFromIri } from "../../utils/extractFromIri";
import { formatDate } from "../../utils/constants";
import { useGetParticipants } from "../../hooks/participants/useGetParticipants";
import {
  buildParticipantsById,
  getContractParticipantLabel,
} from "../PrivateArea/Contracts/utils";

const LandingPage = () => {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [publishers, setPublishers] = useState(0);
  const {
    tableData,
    setTableData,
    searchTerm,
    filters,
    orgProvider,
    pagination,
    setRowCount,
    setPagination,
    rowCount,
  } = useTableControls([]);
  const { isProvider } = useAuthRoles();
  const { mutateAsync: deleteCatalog } = useDeleteCatalog({
    callApiWithWorker,
    t,
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  const { data: pageResponse, isLoading: isCatalogLoading } = useGetCatalog({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const { data: vocabPageResponse, isLoading: isVocabLoading } =
    useGetVocabsPaginated({
      callApiWithWorker,
      currentLang: i18n.language,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });

  const { data: participantsResponse, isLoading: isParticipantsLoading } =
    useGetParticipants({
      callApiWithWorker,
      lang: i18n.language,
      pageIndex: pagination.pageIndex,
      pageSize: 100,
      status: "active",
    });

  const participantsById = useMemo(
    () => buildParticipantsById(participantsResponse?.content),
    [participantsResponse?.content],
  );

  const totalDataset = pageResponse?.totalElements ?? 0;
  const totalDatamodel = vocabPageResponse?.totalElements ?? 0;
  const totalParticipants = participantsResponse?.totalElements ?? 0;

  useEffect(() => {
    const content = pageResponse?.content ?? [];

    const normalized = content.map((item) => ({
      ...item,
      title: getTextByLang(item.title, i18n.language),
      description: getTextByLang(item.description, i18n.language),
      publisher: isParticipantsLoading
        ? t("common.loading")
        : getContractParticipantLabel(
            item.publisher,
            participantsById,
            i18n.language,
          ),
    }));

    setTableData(normalized);
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [
    pageResponse,
    i18n.language,
    isParticipantsLoading,
    participantsById,
    setTableData,
    setRowCount,
    t,
  ]);

  const handleEdit = useCallback((row) => {
    const iriOrId = row?.id ?? row?.identifier;
    const id = extractIdFromIri(iriOrId);

    setSelectedOfferId(id);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedOfferId(null);
  }, []);

  const handleDelete = async (identifier) => {
    const id = extractIdFromIri(identifier);
    try {
      setLoading(true);
      await deleteCatalog({ id });
    } catch (err) {
      showErrorAlert(err, t);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("dataOfferings.offerings.dict.lastUpdate"),
        accessorKey: "updated_at",
        meta: { width: "10%" },
        cell: ({ getValue }) => {
          const date = formatDate(getValue());
          return date;
        },
      },
      {
        header: t("dataOfferings.offerings.dict.name"),
        accessorKey: "title",
        meta: { width: "35%" },
      },
      {
        header: t("dataOfferings.offerings.dict.description"),
        accessorKey: "description",
        meta: { width: "35%" },
      },
      {
        header: t("participant.userAssignment.participantLabel") || "Publisher",
        accessorKey: "publisher",
        meta: { width: "20%" },
      },
    ],
    [t],
  );
  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  useEffect(() => {
    setPublishers(totalParticipants);
    setLoading(false);
  }, [totalParticipants]);

  return (
    <div className="landing-container">
      <div className="charts-table-content">
        <div className="top-cards">
          <div className="card">
            <h4>{t("landing.recurrent")}</h4>
            <br />
            <span>{loading || isParticipantsLoading ? "..." : publishers}</span>
            <p className="description_percentage">
              {t("landing.percentage_org")}
            </p>
          </div>
          <div className="card">
            <h4>{t("landing.dataset_pub")}</h4>
            <br />
            <span>{loading || isCatalogLoading ? "..." : totalDataset}</span>
            <p className="description_percentage">
              {t("landing.num_datasets")}
            </p>
          </div>
          <div className="card">
            <h4>{t("landing.dataModel_publish")}</h4>
            <br />
            <span>{loading || isVocabLoading ? "..." : totalDatamodel}</span>
            <p className="description_percentage">
              {t("landing.percentage_contracts")}
            </p>
          </div>
        </div>
        <h4>{t("datatable.lastOffering")}</h4>
        <div className="w100 bgwhite mt2b3">
          <DataTable
            data={filteredData}
            columns={columns}
            onEdit={(row) => handleEdit(row)}
            onDelete={(row) => handleDelete(row.id)}
            onRowClick={(row) => handleEdit(row)}
            showActions={orgProvider}
            initialSorting={[{ id: "updated_at", desc: true }]}
            pagination={pagination}
            setPagination={setPagination}
            rowCount={rowCount}
            loading={isCatalogLoading || participantsResponse}
          />
        </div>
      </div>
      <OfferDetails
        open={detailsOpen}
        onClose={handleCloseDetails}
        offerId={selectedOfferId}
        callApiWithWorker={callApiWithWorker}
        isProvider={isProvider}
      />
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
